import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';

import {axiosInstance} from '../routes/axiosInstance';
import {excludedDaysOf} from '../utils/excludedDays';

// A market's active excluded (outage) days, fetched once for the Analytics
// page and shared by its notice and charts.
const ExcludedDaysContext = createContext([]);

export function ExcludedDaysProvider({resourceId, children}) {
  const [exclusions, setExclusions] = useState([]);

  useEffect(() => {
    setExclusions([]);
    if (!resourceId) return undefined;
    let stale = false; // a slow answer for a previous market is ignored
    axiosInstance.get(`/market/excluded-days/?resource=${resourceId}`)
      .then((response) => {
        if (!stale) setExclusions(response.data.data || []);
      })
      // Informative only: without access, or on failure, show none.
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [resourceId]);

  return (
    <ExcludedDaysContext.Provider value={exclusions}>
      {children}
    </ExcludedDaysContext.Provider>
  );
}

/** The month's exclusions on every horizon, as listed by the API. */
export function useMonthExclusions(year, month) {
  const exclusions = useContext(ExcludedDaysContext);
  return useMemo(() => {
    const prefix = `${year}-${String(month).padStart(2, '0')}-`;
    return exclusions.filter((e) => e.target_day.startsWith(prefix));
  }, [exclusions, year, month]);
}

/** The excluded days of one horizon in a month: Map of ISO day -> reason. */
export function useExcludedDays(horizon, year, month) {
  const exclusions = useContext(ExcludedDaysContext);
  return useMemo(
    () => excludedDaysOf(exclusions, {horizon, year, month}),
    [exclusions, horizon, year, month],
  );
}
